import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {finalize, tap} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {QueryService} from '../../../core/services/query.service';
import {QueryResourceInterface} from '../../../core/models/query-resource.interface';
import {SearchTabsEnum} from '../../../core/components/search-results-list/search-tabs.enum';
import {AppBreadcrumbService} from '../../navbar/app.breadcrumb.service';
import {ResultsListTabInterface} from '../../../core/components/search-results-list/results-list-tab.interface';
import {AbstractComponent} from "../../../core/components/abstract-component";
import {CacheStatus, CacheStatusService} from "../../../core/services/cache-status.service";
import {CacheRefreshService} from "../../../core/services/cache-refresh.service";
import {PullToRefreshSession} from "../../directives/pull-to-refresh-session";

@Component({
  selector: 'app-search-infinite-list',
  templateUrl: './search-infinite-list.component.html',
  styleUrls: ['./search-infinite-list.component.scss']
})
export class SearchInfiniteListComponent extends AbstractComponent implements OnInit, OnDestroy {
  get selectedTab(): ResultsListTabInterface {
    return this._selectedTab;
  }

  @Input() set selectedTab(value: ResultsListTabInterface) {
    this._selectedTab = value;
    if (this.isInitialLoading || !this.items.length) {
      this.applyPlaceholders();
      return;
    }
    this.setItemsByType(this._selectedTab.type);
  }

  constructor(
    public readonly queryService: QueryService,
    private readonly route: ActivatedRoute,
    private readonly breadcrumb: AppBreadcrumbService,
    private readonly cacheStatusService: CacheStatusService,
    private readonly cacheRefresh: CacheRefreshService
  ) {
    super();
  }

  private activePullRefresh?: PullToRefreshSession;
  public page = 2;
  public totalItems: {[key: string]: number} = {posts: 0, projects: 0, users: 0, events: 0, marketplace: 0};
  public items: QueryResourceInterface[] = [];
  public placeholders: QueryResourceInterface[] = Array.from({ length: 8 }, () => this.createPlaceholder());
  public query: any;
  public isInitialLoading = false;
  public cacheStatus: CacheStatus | null = null;
  private _selectedTab: ResultsListTabInterface;
  @Input() public noResultsMsg: string;
  @Input() public searchTypes: string[] = [];
  @Input() public exactTag: string | null = null;
  public endOfResults = false;
  public loading: boolean = true;
  ngOnInit(): void {
    this.subscriptions.push(this.route.queryParams.subscribe(({q}: {q: string}) => {
      this.query = q;
      this.initList();
    }));
    this.subscriptions.push(this.cacheStatusService.status$.subscribe((event) => {
      if (!event) {
        return;
      }
      this.cacheStatus = event.status;
    }));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.items = [];
    this.page = 2;
    this.queryService.resetCachedResults();
    this.endOfResults = false;
  }

  initList(): void {
    this.activePullRefresh?.cancel(false);
    this.breadcrumb.filtersVisible = this.selectedTab.filtersAvailable;
    this.queryService.resetCachedResults();
    this.loading = false;
    this.isInitialLoading = true;
    this.cacheStatus = null;
    this.endOfResults = false;
    this.applyPlaceholders();
    this.getCollection(1);
  }

  onPullToRefresh(pullToRefresh: PullToRefreshSession): void {
    this.activePullRefresh = pullToRefresh;
    this.cacheRefresh.requestRefresh();
    this.subscriptions.push(this.queryService.prepareRefreshResources(this.query, this.resolveRequestTypes(), this.exactTag).pipe(
      pullToRefresh.operator(),
      finalize(() => pullToRefresh.complete()),
    ).subscribe({
      next: commit => {
        commit();
        this.page = 2;
        this.totalItems = this.queryService.totalItems;
        this.setItemsByType(this.selectedTab.type);
        this.endOfResults = !this.items.length || this.items.length >= this.totalItems[this.selectedTab.type];
        this.loading = false;
        this.isInitialLoading = false;
      },
      error: () => undefined,
    }));
  }

  requestNextPage(): void {
    if (this.activePullRefresh?.active) return;
    if (this.endOfResults || (this.totalItems[this.selectedTab.type] && this.items.length >= this.totalItems[this.selectedTab.type])) {
      this.endOfResults = true;
      this.setLoading(false);
      return;
    }

    this.setLoading(true);
    const sub = this.queryService.fetchResources(this.query, this.page, this.resolveRequestTypes(), this.exactTag)
      .pipe(tap(() => this.totalItems = this.queryService.totalItems))
      .subscribe({
        next: () => {
        this.setItemsByType(this.selectedTab.type);

        if (!this.items.length) {
          this.endOfResults = true;
        }
        if (this.totalItems[this.selectedTab.type] && this.items.length >= this.totalItems[this.selectedTab.type]) {
          this.endOfResults = true;
        }

        this.page++;
        this.setLoading(false);
        this.isInitialLoading = false;
      },
      error: () => {
        this.setLoading(false);
      }
    });

    this.subscriptions.push(sub);
  }

  private applyPlaceholders(): void {
    const placeholderType = this.mapTabTypeToResourceType(this.selectedTab?.type);
    const placeholders = this.placeholders.map((item) => ({
      ...item,
      type: placeholderType
    }));
    this.items = [...placeholders];
  }

  private createPlaceholder(): QueryResourceInterface {
    return {
      isPlaceholder: true,
      thumbnail: ''
    };
  }

  private mapTabTypeToResourceType(type?: SearchTabsEnum): QueryResourceInterface['type'] | undefined {
    switch (type) {
      case SearchTabsEnum.EVENTS:
        return 'event';
      case SearchTabsEnum.POSTS:
        return 'post';
      case SearchTabsEnum.PROJECTS:
        return 'project';
      case SearchTabsEnum.USERS:
        return 'user';
      case SearchTabsEnum.MARKETPLACE:
        return 'marketplace';
      default:
        return undefined;
    }
  }

  private resolveRequestTypes(): string[] {
    switch (this.selectedTab?.type) {
      case SearchTabsEnum.POSTS:
        return ['post'];
      case SearchTabsEnum.PROJECTS:
        return ['project'];
      case SearchTabsEnum.USERS:
        return ['user'];
      case SearchTabsEnum.EVENTS:
        return ['event'];
      case SearchTabsEnum.MARKETPLACE:
        return ['advertisement'];
      default:
        return this.searchTypes;
    }
  }

  public getDefaultThumbnailByType(type: string): 'background'|'car'|'avatar' {
    switch (type) {
      case 'events': return  'background';
      case 'projects': return  'car';
      case 'users': return  'avatar';
      default: return 'background';
    }
  }

  private getCollection(page: number, onComplete?: () => void) {
    this.setLoading(false);
    const sub = this.queryService.fetchResources(this.query, page, this.resolveRequestTypes(), this.exactTag)
      .pipe(tap(() => this.totalItems = this.queryService.totalItems))
      .subscribe({
        next: () => {
        this.setItemsByType(this.selectedTab.type);
        if (!this.items.length) {
          this.endOfResults = true;
        }
        if (this.totalItems[this.selectedTab.type] && this.items.length >= this.totalItems[this.selectedTab.type]) {
          this.endOfResults = true;
        }
        this.setLoading(false);
        this.isInitialLoading = false;
        onComplete?.();
      },
      error: () => {
        this.setLoading(false);
        this.isInitialLoading = false;
        onComplete?.();
      }
    });
    this.subscriptions.push(sub);
  }

  private setLoading(value: boolean): void {
    setTimeout(() => {
      this.loading = value;
    }, 0);
  }

  private  setItemsByType(type: string): void {
    switch (type) {
      case SearchTabsEnum.ALL: this.items = this.queryService.resources; break;
      case SearchTabsEnum.POSTS: this.items = this.queryService.posts; break;
      case SearchTabsEnum.EVENTS:  this.items = this.queryService.events; break;
      case SearchTabsEnum.PROJECTS: this.items = this.queryService.projects; break;
      case SearchTabsEnum.USERS: this.items = this.queryService.users; break;
      case SearchTabsEnum.MARKETPLACE: this.items = this.queryService.marketplace; break;
      default: this.items = this.queryService.resources;
    }
  }

  public trackByItem(index: number, item: QueryResourceInterface): string | number {
    return item?.uri || item?.slug || item?.id || `placeholder:${item?.type || 'item'}:${index}`;
  }
}
